import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "next-themes";
import { useCart } from "../context/CartContext";
import { ChevronDown, Menu, Moon, ShoppingCart, Sun, User } from "lucide-react";
import { Button, buttonVariants } from "./ui/button";
import { useNavigate, useLocation } from "react-router-dom";
import { cn } from "../lib/utils";
import { useScroll } from "./ui/use-scroll";
import { openWhatsApp } from "../lib/whatsapp";
import { getCartCount } from "../lib/cartUtils";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "./ui/navigation-menu";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";

const NAV_ITEMS = [
  { name: "Inicio", href: "/" },
  { name: "Tienda", href: "/tienda" },
  { name: "Diagnóstico", href: "/#diagnostico" },
  { name: "Asistente", href: "/ayuda" },
  { name: "Velocidad", href: "/velocidad" },
  {
    name: "Servicios",
    href: "/servicios",
    items: [
      { name: "Packs", href: "/servicios#packs" },
      { name: "Planes", href: "/servicios#planes" },
    ],
  },
  {
    name: "Nosotros",
    href: "/nosotros",
    items: [
      { name: "Trabajos", href: "/nosotros#trabajos" },
      { name: "Reseñas", href: "/nosotros#resenas" },
    ],
  },
  { name: "Contacto", href: "/#contacto" },
];

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [navValue, setNavValue] = useState("");
  const navRootRef = useRef(null);
  const menuPanelRef = useRef(null);
  const { theme, setTheme } = useTheme();
  const { selectedPlan, items, toggleCart } = useCart();
  const [mounted, setMounted] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const scrolled = useScroll(10);
  const navigation = NAV_ITEMS;

  const handleCartToggle = () => {
    toggleCart();
  };

  const closeMenu = () => setIsMenuOpen(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const handlePointerDown = (event) => {
      const panel = menuPanelRef.current;
      if (panel && panel.contains(event.target)) {
        return;
      }
      setIsMenuOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [isMenuOpen]);

  useEffect(() => {
    const root = navRootRef.current;
    if (!root) return undefined;
    if (!navValue) {
      root.style.removeProperty("--nav-viewport-left");
      return undefined;
    }

    const updateViewportLeft = () => {
      const trigger = root.querySelector('[data-state="open"]');
      if (!trigger) return;
      const rootRect = root.getBoundingClientRect();
      const triggerRect = trigger.getBoundingClientRect();
      const center = triggerRect.left - rootRect.left + triggerRect.width / 2;
      root.style.setProperty("--nav-viewport-left", `${center}px`);
    };

    updateViewportLeft();
    window.addEventListener("resize", updateViewportLeft);
    window.addEventListener("scroll", updateViewportLeft, true);
    return () => {
      window.removeEventListener("resize", updateViewportLeft);
      window.removeEventListener("scroll", updateViewportLeft, true);
    };
  }, [navValue]);

  const scrollToSection = (href) => {
    if (!href) return;
    if (href === "/") {
      if (location.pathname === "/") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        navigate("/", { replace: true });
      }
      closeMenu();
      return;
    }
    if (href.includes("#")) {
      const [basePath, hash] = href.split("#");
      const targetHash = `#${hash}`;
      if (location.pathname !== basePath) {
        navigate(`${basePath}${targetHash}`);
      } else {
        const element = document.querySelector(targetHash);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
        window.location.hash = targetHash;
      }
      closeMenu();
      return;
    }
    if (href.startsWith("#")) {
      if (location.pathname !== "/") {
        navigate(`/${href}`);
      } else {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
        window.location.hash = href;
      }
      closeMenu();
      return;
    }

    navigate(href);
    closeMenu();
  };

  const isActiveRoute = (href) => {
    const currentHash = location.hash || window.location.hash;
    if (!href) return false;
    if (href === "/") {
      return location.pathname === "/" && !currentHash;
    }
    if (href.includes("#")) {
      const [basePath, hash] = href.split("#");
      return location.pathname === basePath && currentHash === `#${hash}`;
    }
    if (href.startsWith("#")) {
      return location.pathname === "/" && currentHash === href;
    }
    return location.pathname === href;
  };

  const isServicesActive = () =>
    ["/servicios", "/planes", "/packs"].includes(location.pathname);

  const cartCount = useMemo(() => getCartCount(items, selectedPlan), [items, selectedPlan]);

  return (
    <header
      className={cn(
        "sticky top-0 z-50 mx-auto w-full max-w-7xl border-b border-transparent bg-background/85 supports-[backdrop-filter]:bg-background/70 backdrop-blur-md md:rounded-full md:transition-all md:ease-out",
        {
          "bg-background/95 supports-[backdrop-filter]:bg-background/50 backdrop-blur-lg md:top-4 md:max-w-6xl md:shadow":
            scrolled,
        }
      )}
    >
      <nav
        className={cn(
          "flex h-16 w-full items-center justify-between px-4 md:h-14 md:transition-all md:ease-out",
          { "md:px-2": scrolled }
        )}
      >
        <button
          type="button"
          onClick={() => navigate("/")}
          className="hidden items-center space-x-2 md:flex"
          aria-label="Ir al inicio"
        >
          <img
            src="https://i.imgur.com/upzKk4Y.png"
            alt="RGST Logo"
            className="w-10 h-10 rounded-full"
          />
          <span className="text-sm sm:text-base font-semibold text-foreground">
            RGST
          </span>
        </button>

        <div className="hidden items-center gap-2 md:flex">
          <NavigationMenu
            ref={navRootRef}
            value={navValue}
            onValueChange={(value) => setNavValue(value || "")}
          >
            <NavigationMenuList>
              {navigation.map((item) =>
                item.items ? (
                  <NavigationMenuItem key={item.name}>
                    <NavigationMenuTrigger
                      className={cn(
                        "nav-trigger !text-foreground hover:!text-foreground data-[state=open]:bg-primary/15 data-[state=open]:!text-foreground",
                        (item.name === "Servicios" ? isServicesActive() : isActiveRoute(item.href)) &&
                          "!bg-primary/15"
                      )}
                      onClick={() => scrollToSection(item.href)}
                    >
                      {item.name}
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <ul className="w-56 p-2">
                        {item.items.map((subItem) => (
                          <li key={subItem.name}>
                            <button
                              type="button"
                              onClick={() => scrollToSection(subItem.href)}
                              className={cn(
                                "w-full text-left rounded-md px-3 py-2 text-sm hover:bg-primary/15",
                                subItem.name === "Trabajos" && "mt-2"
                              )}
                            >
                              {subItem.name}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                ) : (
                  <NavigationMenuItem key={item.name}>
                    <button
                      type="button"
                      onClick={() => scrollToSection(item.href)}
                      className={buttonVariants({
                        variant: "ghost",
                        className: cn(
                          "hover:bg-primary/15 bg-transparent",
                          (item.name === "Servicios"
                            ? isServicesActive()
                            : isActiveRoute(item.href)) && "bg-primary/15"
                        ),
                      })}
                    >
                      {item.name}
                    </button>
                  </NavigationMenuItem>
                )
              )}
            </NavigationMenuList>
          </NavigationMenu>
          <Button
            variant="ghost"
            onClick={() => openWhatsApp("Hola, quiero asesoramiento sobre servicios.")}
            className="bg-black text-white hover:bg-black/90 hover:text-white dark:bg-white dark:text-black dark:hover:bg-white/90 dark:hover:text-black"
          >
            Consultar
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className="text-foreground bg-transparent hover:bg-primary/15"
          >
            {mounted && (theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />)}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleCartToggle}
            className="relative text-foreground bg-transparent hover:bg-primary/15"
          >
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-foreground text-background text-[10px] font-semibold flex items-center justify-center">
                {cartCount}
              </span>
            )}
            <ShoppingCart className="h-4 w-4" />
          </Button>
        </div>

        <div className="md:hidden flex items-center gap-2 w-full">
          <button
            type="button"
            onClick={() => scrollToSection("/")}
            className="flex items-center gap-2 text-foreground/80"
            aria-label="Ir al inicio"
          >
            <img
              src="https://i.imgur.com/upzKk4Y.png"
              alt="RGST Logo"
              className="w-8 h-8 rounded-full"
            />
            <span className="text-xs font-semibold tracking-wide">RGST</span>
          </button>
          <div className="ml-auto flex items-center gap-1">
            <Button
              size="icon"
              variant="ghost"
              className="bg-transparent hover:bg-primary/15"
              aria-label="Cuenta"
              type="button"
            >
              <User className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={handleCartToggle}
              className="relative bg-transparent hover:bg-primary/15"
              aria-label="Carrito"
              type="button"
            >
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-foreground text-background text-[10px] font-semibold flex items-center justify-center">
                  {cartCount}
                </span>
              )}
              <ShoppingCart className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="bg-transparent hover:bg-primary/15"
              aria-label="Cambiar tema"
              type="button"
            >
              {mounted && (theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />)}
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="bg-transparent hover:bg-primary/15 border-none shadow-none menu-toggle-button"
              aria-label={isMenuOpen ? "Cerrar menú" : "Abrir menú"}
              type="button"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </nav>

      <div
        className={cn(
          "md:hidden fixed inset-0 z-50",
          isMenuOpen ? "block" : "hidden"
        )}
        onClick={() => setIsMenuOpen(false)}
        onPointerDown={() => setIsMenuOpen(false)}
      >
        <div
          className="absolute inset-0 bg-black/20 pointer-events-auto"
          onClick={() => setIsMenuOpen(false)}
          onPointerDown={() => setIsMenuOpen(false)}
        />
        <div
          className="relative mx-4 mt-2 rounded-2xl border border-border/60 bg-[#f9fbff] text-slate-900 shadow-lg p-4 max-h-[70vh] overflow-y-auto dark:bg-[#07070b] dark:text-slate-100"
          onClick={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
          ref={menuPanelRef}
        >
          <div className="relative z-10">
            <Accordion type="single" collapsible className="grid gap-y-2 text-center">
              {navigation.map((item) =>
                item.items ? (
                  <AccordionItem
                    key={item.name}
                    value={item.name}
                    className="border-b-0 rounded-lg overflow-hidden"
                  >
                    <AccordionTrigger
                      className={cn(
                        "relative w-full items-center justify-center gap-2 px-3 py-2 text-sm font-medium bg-transparent transition-all duration-150 hover:no-underline data-[state=open]:bg-transparent [&>svg]:hidden"
                      )}
                    >
                      <span className="flex-1 text-center">{item.name}</span>
                      <ChevronDown className="absolute right-3 h-4 w-4 text-muted-foreground transition-transform duration-200" />
                    </AccordionTrigger>
                    <AccordionContent className="mt-1 space-y-1">
                      {item.items.map((subItem) => (
                        <button
                          key={subItem.name}
                          type="button"
                          className={cn(
                            "flex w-full select-none justify-center rounded-lg px-2 py-2 text-center text-sm font-medium leading-none outline-none transition-all duration-150 bg-transparent active:font-semibold",
                            isActiveRoute(subItem.href) && "font-semibold",
                            subItem.name === "Trabajos" && "mt-2"
                          )}
                          onClick={() => scrollToSection(subItem.href)}
                        >
                          {subItem.name}
                        </button>
                      ))}
                    </AccordionContent>
                  </AccordionItem>
                ) : (
                  <button
                    key={item.name}
                    type="button"
                    onClick={() => scrollToSection(item.href)}
                    className={cn(
                      "w-full rounded-lg px-2 py-2 text-center text-sm font-medium bg-transparent transition-all duration-150 active:font-semibold",
                      (item.name === "Servicios"
                        ? isServicesActive()
                        : isActiveRoute(item.href)) && "font-semibold"
                    )}
                  >
                    {item.name}
                  </button>
                )
              )}
            </Accordion>
            <div className="mt-3 flex flex-col gap-2 text-center">
              <Button
                variant="ghost"
                className="w-full justify-center bg-transparent text-sm font-medium text-foreground border-0 shadow-none transition-all duration-150 active:font-semibold menu-cart-button"
                onClick={() => {
                  handleCartToggle();
                  setIsMenuOpen(false);
                }}
              >
                Ver carrito
              </Button>
              <Button
                className="w-full justify-center bg-transparent text-sm font-medium text-foreground border-0 shadow-none transition-all duration-150 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none focus:ring-0 focus:ring-offset-0 active:bg-transparent active:font-semibold menu-theme-button data-[state=open]:bg-transparent data-[state=on]:bg-transparent"
                onClick={(event) => {
                  setTheme(theme === "dark" ? "light" : "dark");
                  event.currentTarget.blur();
                }}
              >
                {mounted && (theme === "dark" ? "Modo claro" : "Modo oscuro")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
